
import fb from './gobi-mw-firestore';
import store from '@/store';
import { SchemaData, ExtractorSchema, ExtratorConvertPair, TableSchema, ExtractorItem, TypicalResponse } from './models.def';
import schemax from './modules/schemax';
import uuid from 'uuid';
import userx from './modules/userx';


const db: firebase.firestore.Firestore = fb.db;
const functions: firebase.functions.Functions = fb.functions;

export class Schemas {

	public db: firebase.firestore.Firestore | null = null;
	public functions: firebase.functions.Functions | null = null;

	private _unsubscribeListenToAllSchemas: (() => void) | null = null;

	constructor() {

		// auto init when user.supportLvl >= 1
		if (userx.supportLvl && userx.supportLvl >= 1) {
			this.init();
		}
		store.watch(
			(states, getters) => getters['userx/supportLvl'],
			(newValue, oldValue) => {
				if (!newValue) {
					if (this._unsubscribeListenToAllSchemas) {
						this._unsubscribeListenToAllSchemas();
						this._unsubscribeListenToAllSchemas = null;
					}
					schemax.updateAllSchemas([]);
				} else if (newValue >= 1) {
					this.init();
				}
			});
	}


	public async init() {
		if (!this._unsubscribeListenToAllSchemas) {
			this.listenToAllSchemasSnapshop();
		}
	}

	public listenToAllSchemasSnapshop() {
		this._unsubscribeListenToAllSchemas = db.collection('schemas').orderBy('createdAt', 'desc')
			.onSnapshot((docs) => {
				if (docs.empty) { return; }

				const allSchemas: SchemaData[] = [];
				docs.forEach((doc) => {
					const schemaData: SchemaData = doc.data() as SchemaData;
					if (!schemaData.model) { schemaData.model = '[]'; }
					schemaData.parsedModel = this._getProcessedTables(schemaData.model);
					schemaData.parsedSolution = this._getProcessedTables(schemaData.solution);
					allSchemas.push(schemaData);
				});
				schemax.updateAllSchemas(allSchemas);
			});
	}

	public async addSchema(schema: SchemaData): Promise<TypicalResponse> {
		const assert = fb.assertSupportLvl(10);
		if (!assert.success) { return assert; }

		const { id, parentId, softwareType } = schema;
		const model = '[]';
		const createdAt = Date.now();
		let solution = model;
		if (parentId) {
			const parentSchema = schemax.allSchemas.find((sm) => sm.id === parentId);
			if (!parentSchema) {
				return { success: false, errorCode: 'not-found', errorMessage: 'Parent schema Not Found.' };
			}
			solution = parentSchema.solution;
		}

		const ref = db.doc(`schemas/${id}`);
		if (schemax.allSchemas.find((sm) => sm.id === id)) {
			return { success: false, errorCode: 'already-exists', errorMessage: 'Schema ID already exists.' };
		}
		try {
			await ref.set({ id, parentId, softwareType, model, solution, createdAt });
			return { success: true };
		} catch (error: any) {
			return { success: false, error, errorCode: error.code, errorMessage: error.message };
		}
	}
	public async updateSchema(schema: SchemaData): Promise<TypicalResponse> {
		const assert = fb.assertSupportLvl(10);
		if (!assert.success) { return assert; }

		const currentSchema = schemax.allSchemas.find((sm) => sm.id === schema.id);
		if (!currentSchema) {
			return { success: false, errorCode: 'not-found', errorMessage: 'Schema Not Found.' };
		}
		if (JSON.stringify(currentSchema) === JSON.stringify(schema)) {

			console.log(currentSchema, schema);
			return { success: false, errorCode: 'not-changed', errorMessage: 'Schema Not Changed.' };
		}

		const newSchema = this._getDatabaseSchema(schema);
		const ref = db.doc(`schemas/${schema.id}`);
		try {
			await ref.update(newSchema);
			// TODO: broadcast commands
			return { success: true };
		} catch (error: any) {
			return { success: false, error, errorCode: error.code, errorMessage: error.message };
		}
	}

	public isAHasBAsParentSchema(a: SchemaData, b: SchemaData): boolean {
		if (a.parentId === b.id) { return true; }
		if (a.parentId === null) { return false; }
		const parent = schemax.allSchemas.find((sm) => sm.id === a.parentId)!;
		return this.isAHasBAsParentSchema(parent, b);
	}

	private _getProcessedTables(str: string): TableSchema[] {
		const result = JSON.parse(str);
		for (const table of result) {
			table.id = uuid.v4();
			const ex = table.extractor;
			this._setIdForExtractor(ex);
		}
		return result;
	}
	private _setIdForExtractor(extractor: ExtractorSchema) {
		for (const key in extractor) {
			if (extractor.hasOwnProperty(key)) {
				const item = extractor[key];
				item.unshift(uuid.v4());
				if (item[1] === 'CONVERT') {
					const pairs = item[3]!;
					for (const pair of pairs) {
						pair[2] = uuid.v4();
					}
				} else if (item[1] === 'JOIN') {
					const ex = item[2] as ExtractorSchema;
					this._setIdForExtractor(ex);
				}
			}
		}
	}
	private _getDatabaseSchema(schema: SchemaData) {

		const parsedSolution = this._mergeToResult(schema);

		const model = JSON.stringify(this._getIdStrippedTables(schema.parsedModel!));
		const solution = JSON.stringify(this._getIdStrippedTables(parsedSolution));

		const { id, createdAt, parentId, softwareType } = schema;
		return { id, createdAt, parentId, softwareType, model, solution };
	}

	private _mergeToResult(schema: SchemaData) {
		const { parentId, parsedModel } = schema;

		if (parentId === null) { return parsedModel!; }

		const parentSchema = schemax.allSchemas.find((schemaData) => schemaData.id === parentId);
		if (!parentSchema) { return parsedModel!; }

		let parsedSolution = JSON.parse(JSON.stringify(parentSchema.parsedSolution!)) as TableSchema[];

		for (const tableSchema of parsedModel!) {
			const { table, collection, keyField, type, masterKey, detailPath, extractor } = tableSchema;
			if (type === 'remove') {
				parsedSolution = parsedSolution.filter((ts) => !(ts.table === table && ts.collection === collection));
			} else {
				const parentTable = parsedSolution.find((ts) => ts.table === table && ts.collection === collection);
				if (parentTable) {
					parentTable.keyField = (keyField) ? keyField : parentTable.keyField;
					parentTable.type = (type !== 'inherit') ? type : parentTable.type;
					parentTable.masterKey = (masterKey) ? masterKey : parentTable.masterKey;
					parentTable.detailPath = (detailPath) ? detailPath : parentTable.detailPath;
					for (const key in extractor) {
						if (extractor.hasOwnProperty(key)) {
							const item = extractor[key];
							if (item[2] === null || item[2] === 'null') {
								delete parentTable.extractor[key];
							} else {
								(parentTable.extractor[key] as any) = item.slice();
							}
						}
					}
				} else {
					parsedSolution.push(JSON.parse(JSON.stringify(tableSchema)));
				}
			}

		}
		return parsedSolution;
	}
	private _getIdStrippedTables(tables: TableSchema[]) {
		const result = [];
		if (tables) {
			for (const tableSchema of tables) {
				const { table, collection, keyField, type, masterKey, detailPath } = tableSchema;
				const extractor = getIdStrippedExtractor(tableSchema.extractor);
				result.push({ table, collection, keyField, type, masterKey, detailPath, extractor });
			}
		}

		return result;

		function getIdStrippedExtractor(extractor: ExtractorSchema) {
			const resultExtractor: any = {};
			for (const key in extractor) {
				if (extractor.hasOwnProperty(key)) {
					const oItem = extractor[key] as ExtractorItem;
					const item: any[] = oItem.slice(1);
					if (oItem[1] === 'CONVERT') {
						const pairs: any[] = [];
						for (const pair of oItem[3] as ExtratorConvertPair[]) {
							pairs.push(pair.slice(0, 2));
						}
						item[2] = pairs;
					} else if (oItem[1] === 'JOIN') {
						item[1] = getIdStrippedExtractor(oItem[2] as ExtractorSchema);
					}
					resultExtractor[key] = item;
				}
			}
			return resultExtractor;
		}
	}
}

const schemaz = new Schemas();
export default schemaz;
